import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Breadcrumbs, createTheme, CssBaseline, Grid, Link, ThemeProvider, Typography, useTheme, Avatar, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { BorderVertical as BorderVerticalIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import GraphToolbar from '../Diagram/GraphToolbar';
import ContextMenu from '../ContextMenu';
import { Common } from '../../utils/common';
import { buildGraphObject, graphHandlerInits, graphPrototypeInits, mxConstraintHandlerInit } from '../../utils/graph/graphSettings';
import { registerCustomShapesForStick, stickShapeConfigRegistry } from '../../utils/graph/registerShapes';
import { getConnectedNetName, propagateBestNetName, applyNetLabel, reEvaluateNetName, getAllNetEdges } from '../../utils/graph/netUtils';
import { processPortOverlaps } from '../../utils/graph/portOverlapHandler';
import NetlistDialog from '../SchematicEditor/NetlistDialog';
import AddInstanceDialog from '../Instance/AddInstanceDialog.';
import EdgeColorSelector from '../ColorPallete/EdgeColorSelector';
import StrokeWidth from '../EdgeStyle/StrokeWidth';
import './stickdiagram.css';
import { mxCell, mxEvent, mxGeometry, mxGraph, mxOutline, mxGraphHandler, mxCodec, mxUtils, mxGraphModel, mxPoint } from "../../utils/graph/mxModule";
import { updateViewDiagram, addView, getViewById, getNetlistByViewId } from '../../services/viewService';
import { addCell } from "../../services/cellService"; 
import { toast } from 'react-toastify';
import AddPinDialog from '../Pin/AddPinDialog';
import { decrypt, encrypt } from "../../utils/encryption";
import { VIEW_TYPES } from "../../constants/Constants";
import ValidatorDialog from './ValidatorDialog';
import MinimizedBar from '../Instance/MinimizedBar';

const VIEW_TYPE_ID = 3;

function StickDiagram() {
  const theme = useTheme();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const params = JSON.parse(decrypt(decodeURIComponent(urlParams.get("d"))));
  const { lId, cId, vId, lN, cN, vN } = params;
  const lib_id = lId;
  const cell_id = cId;
  const libName = lN;
  const cellName = cN;
  const stickDiagName = vN;
  
  const empName= localStorage.getItem('employeeName')
  
  const [mode, setMode] = useState(localStorage.getItem('theme-mode') || 'dark');
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [isSelectionEmpty, setIsSelectionEmpty] = useState(true);
  const [hasClipboardContent, setHasClipboardContent] = useState(false);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [graphReady, setGraphReady] = useState(false);
  const [nextInstanceName, setNextInstanceName] = useState("i1");
  const [isEditInstance, setIsEditInstance] = useState(false);
  const [selectedGraphCell, setSelectedGraphCell] = useState(null);
  const [editInstanceName, setEditInstanceName] = useState('');
  const [openAddInstance, setOpenAddInstance] = useState(false);
  const [isDiagramEmpty, setIsDiagramEmpty] = useState(true);
  const [selectedEdgeColor, setSelectedEdgeColor] = useState("")
  const [selectedEdgeWidth, setSelectedEdgeWidth] = useState(2);
  const [openAddPin, setOpenAddPin] = useState(false);
  const [isEditPin, setIsEditPin] = useState(false);
  const [editPinName, setEditPinName] = useState('');
  // const [pinQueue, setPinQueue] = useState([]);
  const [isOutlineVisible, setIsOutlineVisible] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewId, setViewId] = useState(vId);

  const pinQueueRef = useRef([]);
  const pinMapRef = useRef({});

  // Net label dialog state
  const [openNetLabelDialog, setOpenNetLabelDialog] = useState(false);
  const [netLabelValue, setNetLabelValue] = useState('');
  const [editingNetCell, setEditingNetCell] = useState(null);
  const [openNetlistDialog, setOpenNetlistDialog] = useState(false);
  const [netlistData, setNetlistData] = useState(null);
  const [openLVSDialog, setOpenLVSDialog] = useState(false);

  const [isInstanceMinimized, setIsInstanceMinimized] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);

  // Net counter — single graph (no tabs), keyed by 'stick'
  const netCountersRef = useRef({ stick: 1 });
  const noConnCountersRef = useRef({ stick: 1 });
  const isDarkModeRef  = useRef(false);

  const containerRefs = useRef({});
  const graphRefs = useRef({});
  const commonRefs = useRef({});
  const previewRef = useRef(null);
  const outlineRef = useRef({});
  const insertModeRef = useRef(false);
  const insertCellRef = useRef(null);
  const instancesMapRef = useRef({});
  const instanceCountersRef = useRef(0);
  const selectedIconPathRef = useRef('');
  const selectedShapeTypeRef = useRef('');
  const selectedEdgeColorRef = useRef("");
  const selectedEdgeWidthRef = useRef(2);
  const currentInstanceNameRef = useRef('');

  const localTheme = createTheme({
    palette: {
      mode,
    },
  });
  const toggleMode = () => {
    setMode((prev) => {
      const newMode = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme-mode', newMode);
      return newMode;
    });
  };
  
  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAvatarClose = () => {
    setAnchorEl(null);
  };

  const getInitials = (name) => {
    const eName = name.trim().split(" ");
    return (eName[0][0] + eName[1][0]).toUpperCase();
  };

  const normalizeInstanceName = (name) => (name || '').trim().toLowerCase();

  const splitNameAndSuffix = (name) => {
    const trimmed = (name || '').trim();
    const match = trimmed.match(/^(.*?)(\d+)$/);
    if (!match) return { base: trimmed || 'i', number: null };
    return { base: match[1] || 'i', number: Number(match[2]) };
  };

  const isReservedInstanceName = (name) => (name || '').trim() === 'no-conn';

  const isNoConnCell = (cell) => (cell?.style || '').includes('shape=no-conn') || isReservedInstanceName(cell?.value);

  const getInstanceNames = (excludeCell = null) => {
    const names = new Set();

    Object.entries(instancesMapRef.current || {}).forEach(([name, entry]) => {
      if (!excludeCell || entry?.cell !== excludeCell) {
        names.add(normalizeInstanceName(name));
      }
    });

    const graph = graphRefs.current;
    if (graph?.getDefaultParent) {
      graph.getChildVertices(graph.getDefaultParent()).forEach((cell) => {
        if (cell === excludeCell || isNoConnCell(cell)) return;
        const value = (cell.value || '').trim();
        if (value) names.add(normalizeInstanceName(value));
      });
    }

    return names;
  };

  const isInstanceNameAvailable = (name, excludeCell = null) => {
    const trimmed = (name || '').trim();
    if (!trimmed || isReservedInstanceName(trimmed)) return true;
    return !getInstanceNames(excludeCell).has(normalizeInstanceName(trimmed));
  };

  const instanceCounter = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || isReservedInstanceName(trimmed)) return;
    const { base, number } = splitNameAndSuffix(trimmed);
    if (base !== 'i') return;
    instanceCountersRef.current = Math.max(instanceCountersRef.current || 1, (number || 0) + 1);
  };

  const getNextInstanceName = (prefixHint = 'i') => {
    const { base, number } = splitNameAndSuffix(prefixHint);
    const prefix = base || 'i';
    let next = prefix === 'i' && number == null ? (instanceCountersRef.current || 1) : (number == null ? 1 : number + 1);
    let candidate = `${prefix}${next}`;
    const existing = getInstanceNames();

    while (existing.has(normalizeInstanceName(candidate))) {
      next += 1;
      candidate = `${prefix}${next}`;
    }

    if (prefix === 'i') instanceCountersRef.current = next;
    return candidate;
  };

  const registerInstanceName = (name, cell, shapeType, library) => {
    const trimmed = (name || '').trim();
    if (!trimmed || isReservedInstanceName(trimmed)) return;
    instancesMapRef.current[trimmed] = { cell, shapeType, library };
    instanceCounter(trimmed);
  };

  const unregisterInstanceName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    delete instancesMapRef.current[trimmed];
  };

  // Rebuilds the registry after loading existing XML.
  const rebuildInstanceState = (graph) => {
    instancesMapRef.current = {};
    instanceCountersRef.current = 1;
    if (!graph) return;
    graph.getChildVertices(graph.getDefaultParent()).forEach((cell) => {
      if (isNoConnCell(cell)) return;
      registerInstanceName(cell.value, cell, cell.style, cell.library);
    });
  };

  const getCopyInstanceName = (originalName) => {
    const { base, number } = splitNameAndSuffix(originalName);
    const prefixHint = number == null ? `${base}0` : base;
    return getNextInstanceName(prefixHint);
  };

  const updateAllFontAndEdgeColors = (graph, isDarkMode) => {
    const fontColor = isDarkMode ? '#ffffff' : '#000000';
    const circleColor = isDarkMode ? '#517891' : '#000000';
    const model = graph.getModel();
    const parent = graph.getDefaultParent();

    model.beginUpdate();
    try {
      graph.getChildVertices(parent).forEach((vertex) => {
        let style = model.getStyle(vertex) || '';
        style = style.includes('fontColor=') ? style.replace(/fontColor=[^;]*/, `fontColor=${fontColor}`) : style + `;fontColor=${fontColor}`;
        style = style.includes('circleColor=') ? style.replace(/circleColor=[^;]*/, `circleColor=${circleColor}`) : style + `;circleColor=${circleColor}`;
        model.setStyle(vertex, style);
      });

      graph.getChildEdges(parent).forEach((edge) => {
        let style = model.getStyle(edge) || '';
        style = style.includes('fontColor=') ? style.replace(/fontColor=[^;]*/, `fontColor=${fontColor}`) : style + `;fontColor=${fontColor}`;
        model.setStyle(edge, style);
      });
    } finally {
      model.endUpdate();
    }
    graph.refresh();
  };

  const initializeGraph = () => {
    if(graphRefs.current.container !== undefined) return;
    const container = containerRefs.current;
    if (!container) return;
    container.innerHTML = ""; // Clear previous content
    const graph = new mxGraph(container);

    const originalImportCells = graph.importCells.bind(graph);
    graph.importCells = function (cells, dx, dy, target, evt, mapping) {
      // Snapshot existing names BEFORE paste
      const existingBeforePaste = new Set();
      Object.keys(instancesMapRef.current || {}).forEach(n => {
        existingBeforePaste.add(normalizeInstanceName(n));
      });
      graph.getChildVertices(graph.getDefaultParent()).forEach((cell) => {
        if (isNoConnCell(cell)) return;
        const value = (cell.value || '').trim();
        if (value) existingBeforePaste.add(normalizeInstanceName(value));
      });

      // Wrap everything in single beginUpdate so paste + rename = one undo step
      graph.getModel().beginUpdate();
      const importedCells = originalImportCells(cells, dx, dy, target, evt, mapping);
      try {
        const assignedThisPaste = new Set();

        (importedCells || []).forEach((cell) => {
          if (!cell?.isVertex?.() || isNoConnCell(cell)) return;
          if (cell.getParent?.() !== graph.getDefaultParent()) return;

          const oldName = (cell.value || '').trim();
          const { base } = splitNameAndSuffix(oldName);

          if (base !== 'i') {
            // Custom name — keep if free, else find next available with same prefix
            if (!existingBeforePaste.has(normalizeInstanceName(oldName)) &&
                !assignedThisPaste.has(normalizeInstanceName(oldName))) {
              // Free — keep original name
              registerInstanceName(oldName, cell, cell.style, cell.library);
              assignedThisPaste.add(normalizeInstanceName(oldName));
              return;
            }
            // Conflict — find next with same prefix
            const { number } = splitNameAndSuffix(oldName);
            let next = 1;
            let candidate = `${base}${next}`;
            while (
              existingBeforePaste.has(normalizeInstanceName(candidate)) ||
              assignedThisPaste.has(normalizeInstanceName(candidate))
            ) {
              next += 1;
              candidate = `${base}${next}`;
            }
            graph.getModel().setValue(cell, candidate);
            registerInstanceName(candidate, cell, cell.style, cell.library);
            assignedThisPaste.add(normalizeInstanceName(candidate));
            return;
          }

          // 'i' prefix — always find next available from i1
          let next = 1;
          let candidate = `i${next}`;
          while (
            existingBeforePaste.has(normalizeInstanceName(candidate)) ||
            assignedThisPaste.has(normalizeInstanceName(candidate))
          ) {
            next += 1;
            candidate = `i${next}`;
          }
          graph.getModel().setValue(cell, candidate);
          registerInstanceName(candidate, cell, cell.style, cell.library);
          assignedThisPaste.add(normalizeInstanceName(candidate));
        });
      } finally {
        graph.getModel().endUpdate();
      }

      return importedCells;
    };

    createOutlineContainer(graph);

    graph.setEnabled(true);

    // Wire net naming
    graph.connectionHandler.insertEdge = function (parent, id, value, source, target, style) {
      if (!netCountersRef.current.stick) netCountersRef.current.stick = 1;
      const sourceNet = getConnectedNetName(graph, source, noConnCountersRef, 'stick');
      const targetNet = getConnectedNetName(graph, target, noConnCountersRef, 'stick');
      const inheritedNetName = (sourceNet?.startsWith('no-conn_') ? sourceNet : null) || (targetNet?.startsWith('no-conn_') ? targetNet : null) || sourceNet || targetNet;
      const autoNetName = inheritedNetName || `net_${netCountersRef.current.stick++}`;

      const edge = graph.createEdge(parent, id, autoNetName, source, target, style);
      edge.geometry = new mxGeometry();
      edge.geometry.relative = true;
      const edgeFontColor = isDarkModeRef.current ? '#ffffff' : '#000000';
      edge.style = (edge.style || '') + `;noLabel=1;fontColor=${edgeFontColor};labelBackgroundColor=none`;

      graph.getModel().beginUpdate();
      try {
        graph.addEdge(edge, parent, source, target);
        if (!target) { graph.removeCells([edge]); return null; }
        propagateBestNetName(graph, edge, source, target);
        return edge;
      } finally {
        graph.getModel().endUpdate();
      }
    };

    // Re-evaluate surviving net names when wires are deleted
    const _originalRemoveCells = graph.removeCells.bind(graph);
    graph.removeCells = function (cells, includeEdges) {
      const model = graph.getModel();
      const deletedIds = new Set((cells || []).map(c => c.id));
      const removedInstanceNames = [];
      (cells || []).forEach((cell) => {
        if (!model.isVertex(cell) || isNoConnCell(cell)) return;
        if (model.getParent(cell) === graph.getDefaultParent()) {
          removedInstanceNames.push(cell.value);
        }
      });

      const removalInfo = [];
      (cells || []).forEach(cell => {
        if (!model.isEdge(cell)) return;
        removalInfo.push([model.getTerminal(cell, true), model.getTerminal(cell, false)]);
      });

      const result = _originalRemoveCells(cells, includeEdges);
      removedInstanceNames.forEach(unregisterInstanceName);


      // Recalculate counter from remaining instances so it doesn't get stuck
      const remainingNames = getInstanceNames();
      let maxCounter = 0;
      remainingNames.forEach(name => {
        const { base, number } = splitNameAndSuffix(name);
        if (base === 'i' && number != null) maxCounter = Math.max(maxCounter, number);
      });
      instanceCountersRef.current = maxCounter + 1;  // next available

      const processed = new Set();
      const evalFromTerminal = (terminal) => {
        if (!terminal || processed.has(terminal.id)) return;
        processed.add(terminal.id);
        let seedEdge = null;
        if (model.isEdge(terminal) && model.getParent(terminal) !== null) {
          seedEdge = terminal;
        } else if (model.isVertex(terminal)) {
          const edges = graph.getEdges(terminal).filter(e => model.getParent(e) !== null);
          if (edges.length > 0) seedEdge = edges[0];
        }
        if (seedEdge) reEvaluateNetName(graph, seedEdge, netCountersRef, 'stick');
      };

      removalInfo.forEach(([src, tgt]) => {
        evalFromTerminal(src);
        evalFromTerminal(tgt);
      });

      return result;
    };

    graphRefs.current = graph;
    graphRefs.current.view_type_id = VIEW_TYPES.stick;
    // commonRefs.current[tabId] = new Common(graph);
    commonRefs.current = new Common(graph, (hasContent) => {
      setHasClipboardContent(hasContent);
    });

    // Set up change listener to track modifications
    graph.getModel().addListener(mxEvent.CHANGE, () => {
      setSaveEnabled(true);
    });
    // Track selection changes separately
    graph.getSelectionModel().addListener(mxEvent.CHANGE, () => {
      setIsSelectionEmpty(graph.isSelectionEmpty());
    });

    setGraphReady(true);


    graphHandlerInits(graphRefs.current, selectedEdgeColorRef ,selectedEdgeWidthRef);
    mxConstraintHandlerInit(isDarkMode);
    buildGraphObject(graphRefs.current);
    registerCustomShapesForStick();
    addGraphListeners(graphRefs.current);   //This is not moved to common class intentionally   
  };

  useEffect(() => {
    graphPrototypeInits(selectedEdgeColorRef, selectedEdgeWidthRef, isDarkMode);
  }, []);

  useEffect(() => {
    if (instanceCountersRef.current === undefined) {
      instanceCountersRef.current = 1;
    }
    setNextInstanceName(getNextInstanceName('i'));
    setIsEditInstance(false);
    initializeGraph();
    const container = containerRefs.current;
    //To enable the top, right, bottom, left keys to move the cells/vertex
    // Register key handlers
    Object.entries(commonRefs.current).forEach(([common]) => {
          common?.registerKeyHandlers?.();        
      });
    container?.focus();

    const handleBeforeUnload = (event) => {
      if (saveEnabled) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [saveEnabled]);

  useEffect(() => {
    if (!graphRefs.current) return;
    // Graph model change listener
    const modelListener = () => checkIfDiagramIsEmpty();
    graphRefs.current.getModel().addListener(mxEvent.CHANGE, modelListener);

    commonRefs.current?.delete(instancesMapRef);
    const handleMouseMove = (e) => {

      if (insertModeRef.current && previewRef.current && containerRefs.current) {
        const rect = containerRefs.current.getBoundingClientRect();
        const isInsideGraph =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
        const isOverDialog = e.target.closest('.MuiDialog-container, .MuiPopover-root, .MuiModal-root') !== null;
        if (isInsideGraph && !isOverDialog) {
          const offsetX = e.clientX - rect.left;
          const offsetY = e.clientY - rect.top;
          previewRef.current.style.left = `${offsetX - 30}px`;
          previewRef.current.style.top = `${offsetY - 45}px`;
          previewRef.current.style.display = "block";
        } else {
          previewRef.current.style.display = "none";
        }
      }
    };

    const handleRightClick = (e) => {
      insertModeRef.current = false;
      insertCellRef.current = null;
      if (previewRef.current) {
        e.preventDefault(); // prevent the default context menu
        previewRef.current.remove();
        previewRef.current = null;
      }
      handleCloseInstance();
      setDialogKey(prev => prev + 1);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("contextmenu", handleRightClick);
    // Cleanup both listeners
    return () => {
      if (graphRefs.current !== undefined) {
        graphRefs.current.getModel().removeListener(modelListener);
      }

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("contextmenu", handleRightClick);
    };
  }, [graphRefs.current]);

      //triggers during the light/dark mode toggle
  useEffect(() => {
    mxGraphHandler.prototype.previewColor = isDarkMode ? '#ffffff' : '#000000';
    selectedEdgeColorRef.current = selectedEdgeColor || "#38befe";
    if (!graphRefs.current) return;
     const graph = graphRefs.current;
     const selectedCells = graph.getSelectionCells();
     const parent = graphRefs.current.getDefaultParent();
    const edgeCells=selectedCells.filter(cell => graph.getModel().isEdge(cell));
    const edgeStrokeColor = selectedEdgeColor || "#38befe"
    const strokeWidth = selectedEdgeWidth || "2";
    const fontColor = isDarkMode ? '#ffffff' : '#000000';
    mxConstraintHandlerInit(isDarkMode);
    updateAllFontAndEdgeColors(graphRefs.current, isDarkMode);
   
    const model = graphRefs.current.getModel();
    

    model.beginUpdate();
    try {
      // const allEdges = graphRefs.current.getChildEdges(parent);

      edgeCells.forEach((edge) => {
        let style = model.getStyle(edge) || "";

        // Replace or add strokeColor in style string
        if (style.includes("strokeColor=")) {
          style = style.replace(/strokeColor=[^;]*/, `strokeColor=${edgeStrokeColor}`);
        } else {
          style += `;strokeColor=${edgeStrokeColor}`;
        }
        // Update fontColor
        if (style.includes("fontColor=")) {
          style = style.replace(/fontColor=[^;]*/, `fontColor=${fontColor}`);
        } else {
          style += `;fontColor=${fontColor}`;
        }
        // Stroke width
        if (style.includes("strokeWidth=")) {
          style = style.replace(/strokeWidth=[^;]*/, `strokeWidth=${strokeWidth}`);
        } else {
          style += `;strokeWidth=${strokeWidth}`;
        }

        model.setStyle(edge, style);
      });

      // Update all vertices (instances)
      const allVertices = graph.getChildVertices(parent);
      allVertices.forEach((vertex) => {
        let style = model.getStyle(vertex) || "";

        // Update fontColor
        if (style.includes("fontColor=")) {
          style = style.replace(/fontColor=[^;]*/, `fontColor=${fontColor}`);
        } else {
          style += `;fontColor=${fontColor}`;
        }
        
        const circleColor = isDarkMode ? '#517891' : '#000000';
        if (style.includes("circleColor=")) {
          style = style.replace(/circleColor=[^;]*/, `circleColor=${circleColor}`);
        } else {
          style += `;circleColor=${circleColor}`;
        }

        model.setStyle(vertex, style);
      })

    } finally {
      model.endUpdate();
    }
    if (containerRefs.current) {
      containerRefs.current.style.height = `${containerRefs.current.scrollHeight}px`;
    }
    graphRefs.current.refresh();
  }, [mode , selectedEdgeColor, selectedEdgeWidth]);

  useEffect(() => {
    if (!viewId || viewId === 'undefined' || isNaN(Number(viewId))) return;
    if (!graphReady) return;

    const loadExistingView = async () => {
      try {
        const response = await getViewById(viewId);
        if(!response.success) return;
        var data = response.body;
        const diagramXml = data.diagram_xml;

        if (diagramXml && graphRefs.current) {
          const xmlDoc = mxUtils.parseXml(diagramXml);
          const decoder = new mxCodec(xmlDoc);
          const graph = graphRefs.current;

          window["mxGraphModel"] = mxGraphModel;
          window["mxGeometry"] = mxGeometry;
          window["mxPoint"] = mxPoint;

          graph.getModel().beginUpdate();
          try {
            decoder.decode(xmlDoc.documentElement, graph.getModel());
          } finally {
            graph.getModel().endUpdate();
          }
          rebuildInstanceState(graph);
          updateAllFontAndEdgeColors(graph, isDarkMode);

          // This prevents undo from reverting the loaded diagram
          if (commonRefs.current) {
            commonRefs.current._undoManager.clear();
          }
          setSaveEnabled(false);
        }
      } catch (err) {
        console.error("Failed to load existing view:", err);
        toast.error("Could not load diagram.");
      }
    };

    loadExistingView();
  }, [graphReady, viewId]);

  const checkIfDiagramIsEmpty = () => {
    if (graphRefs.current) {
      const hasVertices =
        graphRefs.current.getChildVertices(graphRefs.current.getDefaultParent()).length === 0;
      setIsDiagramEmpty(hasVertices);
    }
  };

  function addGraphListeners( graph) {

    graph.addListener(mxEvent.CLICK, function (sender, evt) {
      const mouseEvent = evt.getProperty('event');
      if (mouseEvent.button !== 0 || !insertModeRef.current || !insertCellRef.current) return;
      const pt = graph.getPointForEvent(mouseEvent);
      const originalCell = insertCellRef.current;
      const clone = graph.getModel().cloneCell(originalCell);

      // Offset to center shape under cursor
      const width = clone.geometry.width;
      const height = clone.geometry.height;

      clone.geometry.x = pt.x - width / 2;
      clone.geometry.y = pt.y - height / 2;

      const library = insertCellRef.current.library || '';
      const isPlacingPins = pinQueueRef.current.length > 0;

      if (isPlacingPins) {
        // --- Pin Placement ---
        const currentLabel = pinQueueRef.current[0];
        clone.value = currentLabel;

        graph.getModel().beginUpdate();
        try {
          graph.addCell(clone);
          graph.setSelectionCell(clone);
        } finally {
          graph.getModel().endUpdate();
        }

        // Remove placed pin label from queue
        pinQueueRef.current.shift();
        // setPinQueue([...pinQueueRef.current]);

        if (pinQueueRef.current.length === 0) {
          // No more pins to place — clear everything
          clearPinPlacement();
          return;
        }

        // Show next pin ghost
        previewRef.current?.remove();
        previewRef.current = null;

        setTimeout(() => {
          const nextLabel = pinQueueRef.current[0];
          createGhostPin(
            selectedShapeTypeRef.current,
            selectedIconPathRef.current,
            nextLabel,
            library
          );
        }, 50);

      } else {
          // CLEAR OLD PIN SELECTION
          graph.clearSelection();
          setSelectedGraphCell(null);
          setIsEditPin(false);
        // --- Instance Placement ---
        const typedName = currentInstanceNameRef.current?.trim();
        let instanceName;
        if (selectedShapeTypeRef.current === "no-conn") {
          instanceName = "no-conn"; 
        } else {
          instanceName = typedName || getNextInstanceName('i');
          if (!isInstanceNameAvailable(instanceName)) return;
        }

        clone.value = instanceName;

        registerInstanceName(instanceName, clone, originalCell.style, library);

        graph.getModel().beginUpdate();
        try {
          graph.addCell(clone);
          graph.setSelectionCell(clone);
        } finally {
          graph.getModel().endUpdate();
        }
          // FIX: reset edit mode
          setIsEditInstance(false);
        if (selectedShapeTypeRef.current !== "no-conn") {
          const nextName = getNextInstanceName('i');
          currentInstanceNameRef.current = nextName;   // reset ref to next valid name
          setNextInstanceName(nextName);
        }
      }
    });

    graph.addListener(mxEvent.DOUBLE_CLICK, function (sender, evt) {
      const cell = evt.getProperty('cell');
      if (!cell) return;
      evt.consume(); // Prevent mxGraph default in-place editing
      const model = graph.getModel();

      // Edge (wire) double-click → net label dialog
      if (model.isEdge(cell) && !cell.internal) {
        graph.connectionHandler.reset();
        setEditingNetCell(cell);
        setNetLabelValue(cell.value || '');
        setOpenNetLabelDialog(true);
        return;
      }

      if (model.isVertex(cell)) {
        const style = cell.style || "";
        const parent = model.getParent(cell);

        // Port ellipse child (child of an instance) → net label dialog
        if (parent && model.isVertex(parent) && model.getParent(parent) !== null &&
            style.includes('shape=ellipse')) {
          graph.connectionHandler.reset();
          setEditingNetCell(cell);
          setNetLabelValue(cell.value || '');
          setOpenNetLabelDialog(true);
          return;
        }

        const isPin = style.includes("shape=in") || style.includes("shape=out") || style.includes("shape=inout");
        if (isPin) {
          setSelectedGraphCell(cell);
          setEditPinName(cell.value);
          setIsEditPin(true);
          setOpenAddPin(true);
        } else {
          setSelectedGraphCell(cell);
          setNextInstanceName(cell.value);
          setEditInstanceName(cell.value);
          setIsEditInstance(true);
          setOpenAddInstance(true);
        }
      }
    });

    graph.addListener(mxEvent.CELLS_MOVED, function (sender, evt) {
      const cells = evt.getProperty('cells');

      if (!cells || cells.length === 0) return;
      setTimeout(() => {
        processPortOverlaps(graphRefs.current, 5 , selectedEdgeColorRef.current || "#38befe", selectedEdgeWidth || "2",isDarkModeRef.current);
      }, 0);

    });

  }
  const createOutlineContainer = (graph) => {
    const outlineContainer = outlineRef.current;
    if (outlineContainer && graph) {
      new mxOutline(graph, outlineContainer);
    }
  };
  const isDarkMode = (mode === 'dark');
  isDarkModeRef.current = isDarkMode;

  // Net label dialog handlers
  const handleNetLabelClose = () => {
    setOpenNetLabelDialog(false);
    setEditingNetCell(null);
    setNetLabelValue('');
  };

  const handleNetLabelConfirm = () => {
    if (!editingNetCell) { handleNetLabelClose(); return; }
    const graph = graphRefs.current;
    if (!graph) { handleNetLabelClose(); return; }

    const model = graph.getModel();
    model.beginUpdate();
    try {
      applyNetLabel(graph, editingNetCell, netLabelValue.trim(), isDarkMode, netCountersRef, 'stick');
    } finally {
      model.endUpdate();
    }

    handleNetLabelClose();
  };

  // Stick Netlist
  const handleShowNetlist = async () => {
    if (!viewId) {
      toast.warning("Please save the diagram before viewing the netlist.");
      return;
    }
    try {
      const response = await getNetlistByViewId(viewId, cellName);
      if (!response.success) {
        toast.error("Failed to generate netlist.");
        return;
      }
      const { spice } = response.body.data;
      setNetlistData(spice);
      setOpenNetlistDialog(true);
    } catch (err) {
      console.error("Netlist fetch error:", err);
      toast.error("Failed to generate netlist.");
    }
  };

  const handleCopyNetlist = () => {
    navigator.clipboard.writeText(netlistData || "").catch(() => {});
  };

  const handleContextMenu = (event) => {
    if (previewRef.current) {
      return;
    }
    event.preventDefault();
    const container = containerRefs.current;
    const rect = container.getBoundingClientRect();
    setMenuPosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
    setMenuVisible(true);
  };
  const handleCloseMenu = () => setMenuVisible(false);
  const handleMenuClick1 = (adjustedPosition = null) => {
    if (adjustedPosition) {
      setMenuPosition(adjustedPosition);
    }
  };

  const handleCellViewSelect = (data) => {
    if (!data) {
      // Cleanup if null or undefined is passed
      insertModeRef.current = false;
      insertCellRef.current = null;

      if (previewRef.current) {
        previewRef.current.remove();
        previewRef.current = null;
      }

      return; // Stop further execution
    }

    const { shapeType, imagePath, label, library } = data;

    handleInstanceClick(shapeType, imagePath, label, library); // existing logic for preview and placement
  };
  
  const handleInstanceRename = (newName) => {
    const trimmedName = (newName || '').trim();
    currentInstanceNameRef.current = trimmedName;
    // Guard — if no cell selected
    if (!selectedGraphCell) {
      currentInstanceNameRef.current = trimmedName;
      return;
    }
    if (!selectedGraphCell || trimmedName === (selectedGraphCell.value || '').trim()) return;

    const oldName = selectedGraphCell.value;

    if (!isInstanceNameAvailable(trimmedName, selectedGraphCell)) {
      console.warn("Instance name already exists.");
      return;
    }

    const graph = graphRefs.current;
    if (!graph) return;

    graph.getModel().beginUpdate();
    try {
      graph.getModel().setValue(selectedGraphCell, trimmedName);
      graph.refresh(selectedGraphCell);
    } finally {
      graph.getModel().endUpdate();
    }

    unregisterInstanceName(oldName);
    registerInstanceName(trimmedName, selectedGraphCell, selectedGraphCell.style, selectedGraphCell.library);

    setNextInstanceName(trimmedName);
  };

  const handleInstanceClick = (shapeType, iconPath, label, library) => {
    selectedShapeTypeRef.current = shapeType;
    selectedIconPathRef.current = iconPath;

    const fontColor = isDarkMode ? '#ffffff' : '#000000';
    const circleColor = isDarkMode ? '#517891' : '#000000';

    const shapeConfig = stickShapeConfigRegistry[shapeType];

    // Default style
    let style = `shape=${shapeType}; labelPosition=right;align=right;spacingBottom=-30;fontColor=${fontColor};circleColor=${circleColor}`;

    // Override style from config (for no-conn)
    if (shapeConfig?.style) {
      style = typeof shapeConfig.style === "function"
        ? shapeConfig.style(fontColor)
        : shapeConfig.style;
    }

    let vertex = null;

    if (shapeConfig) {
      vertex = new mxCell(
        label || "",
        new mxGeometry(0, 0, shapeConfig.width, shapeConfig.height),
        style
      );

      if (shapeConfig.ports?.length) {
        const portStyle = `shape=ellipse;perimeter=ellipsePerimeter;resizable=0;movable=0;connectable=1;fillColor=#21b04c;strokeColor=#21b04c;fontColor=${fontColor}`;

        shapeConfig.ports.forEach(({ name, label: portLabel, x, y }) => {
          const pinGeo = new mxGeometry(x, y, 10, 10);
          pinGeo.relative = true;
          pinGeo.offset = new mxPoint(-5, -5);

          const pinCell = new mxCell(portLabel || "", pinGeo, portStyle);

          if (name) pinCell.name = name;
          pinCell.setVertex(true);          
          // pinCell.value = name;

          pinCell.setConnectable(true);

          vertex.insert(pinCell);
        });

        // only ports connectable
        vertex.setConnectable(false);
      }
    } 
    else {
      // fallback
      vertex = new mxCell("", new mxGeometry(0, 0, 120, 70), style);
    }

    vertex.setVertex(true);
    insertModeRef.current = true;
    vertex.library = library;
    insertCellRef.current = vertex;

    const ghost = document.createElement("img");
    ghost.src = iconPath;
    ghost.style.position = "absolute";
    ghost.style.pointerEvents = "none";
    ghost.style.opacity = "0.6";
    ghost.style.zIndex = "9999";

    if (shapeConfig) {
      ghost.style.width  = `${shapeConfig.width}px`;
      ghost.style.height = `${shapeConfig.height}px`;
    } else {
      ghost.style.width = "120px";
      ghost.style.height = "70px";
    }

    const container = containerRefs.current;
    if (container) {
      container.appendChild(ghost);
      previewRef.current = ghost;
    }

    previewRef.current = ghost;

    let instanceName = "";

    if (shapeType === "no-conn") {
      instanceName = "no-conn";
    } else {
      instanceName = getNextInstanceName('i');
    }

    setNextInstanceName(instanceName);

    let lastX = 0, lastY = 0;

    const moveGhost = () => {
      if (!container) return;

      const { scrollLeft, scrollTop } = container;
      const { left, top } = container.getBoundingClientRect();

      const x = lastX - left + scrollLeft - ghost.offsetWidth * 0.5;
      const y = lastY - top + scrollTop - ghost.offsetHeight * 0.5;

      ghost.style.left = x + "px";
      ghost.style.top  = y + "px";
    };

    window.addEventListener("mousemove", e => {
      lastX = e.clientX;
      lastY = e.clientY;
      moveGhost();
    });

    container.addEventListener("scroll", moveGhost);
  };

  const handleOpenAddInstance = () => {
    setSelectedGraphCell(null);
    setNextInstanceName(getNextInstanceName('i'));
    setIsEditInstance(false);
    setIsInstanceMinimized(false);
    setOpenAddInstance(true);

  };

  const handleHideInstance = () => {
    setOpenAddInstance(false);
    setIsInstanceMinimized(true);
  };

  const handleRestoreInstance = () => {
    setIsInstanceMinimized(false);
    setOpenAddInstance(true);
  };

  const handleCloseInstance = () => {
    setIsInstanceMinimized(false);
    setOpenAddInstance(false);
  };
  const handleEdgeColorChange = (color) => {
    selectedEdgeColorRef.current = color; // sync ref
    setSelectedEdgeColor(color); // triggers useEffect
  };

  const handleEdgeWidthChange = (width) => {
    selectedEdgeWidthRef.current = width;
    setSelectedEdgeWidth(width);
  };

  const handleOutlineVisible = (isVisible) => {
    setIsOutlineVisible(isVisible);
  }


  const handleSave = async () => {
    const graph = graphRefs.current;
    if (!graph) return;

    const encoder = new mxCodec();
    const node = encoder.encode(graph.getModel());
    const xml = mxUtils.getXml(node);

    try {
      let finalCellId = cell_id;

      if (viewId) {
        // PUT: Update existing view
        const response = await updateViewDiagram({
          view_id:viewId,
          // diagram: { field: "updated json" }, // Quick fix
          diagram_xml: xml,
        });
          if(!response.success) return;

        toast.success("Your stick diagram is updated!");
      } else {

        // If cellId is not present, create a new cell
        if (!cell_id) {        
          const cellRes = await addCell({
            name: cellName,
            library_id: lib_id,
          });
          if (!cellRes.success) return;
            const data = cellRes.body;
          finalCellId = data.cell_id;
        }

        // POST: create new view
        const response = await addView({
          name: stickDiagName,
          cell_id: finalCellId,
          view_type_id: VIEW_TYPE_ID,
          // diagram: { field: "sample json" }, // Quick fix
          diagram_xml: xml,
        });
        const newViewId = response.body.view_id;
        if (newViewId) {
          setViewId(newViewId);

          // Persist ids into the URL so refresh stays in edit-mode
          const nextParams = {
            lId: lib_id,
            cId: finalCellId,
            vId: newViewId,
            lN: libName,
            cN: cellName,
            vN: stickDiagName,
          };
          const encryptedParams = encodeURIComponent(encrypt(JSON.stringify(nextParams)));
          window.history.replaceState(null, "", `${window.location.pathname}?d=${encryptedParams}`);
        }
        if(!response.success) return;

        toast.success("Your stick diagram is created!");
      }

      setSaveEnabled(false);
    } catch (err) {
      console.error("Save failed:", err);
      if (err.response?.data) {
        console.error("Server response error data:", err.response.data);
        toast.error(err.response.data.message || "Failed to save view.");
      } else {
        toast.error("Failed to save view. Please try again.");
      }
    }
  };

  const clearPinPlacement = () => {
    insertModeRef.current = false;
    insertCellRef.current = null;
    pinQueueRef.current = [];
    // setPinQueue([]);

    if (previewRef.current) {
      previewRef.current.remove();
      previewRef.current = null;
    }

    // Remove ghost event listeners
    if (window.moveGhostHandler) {
      window.removeEventListener("mousemove", window.moveGhostHandler);
      window.moveGhostHandler = null;
    }

    const container = containerRefs.current;
    if (container && window.scrollGhostHandler) {
      container.removeEventListener("scroll", window.scrollGhostHandler);
      window.scrollGhostHandler = null;
    }

    selectedShapeTypeRef.current = '';
    selectedIconPathRef.current = '';
  };

  const handleOpenAddPin = () => {
    setOpenAddPin(true);
    setIsEditPin(false);
    setEditPinName('');
  };

  const handlePinClick = (shapeType, iconPath, labels, library) => {
    if (!labels || labels.length === 0) return;

    // setPinQueue(labels); // store full queue
    pinQueueRef.current = labels;

    const firstLabel = labels[0];
    createGhostPin(shapeType, iconPath, firstLabel, library); // extracted logic below
  };

  const createGhostPin = (shapeType, iconPath, label, library) => {
    let height = 20;
    let width = shapeType === "inout" ? 40 : 30;

    selectedShapeTypeRef.current = shapeType;
    selectedIconPathRef.current = iconPath;

    const fontColor = isDarkMode ? '#ffffff' : '#000000';
    const labelPosition = shapeType === 'in' ? 'left' : 'right';
    const style = `shape=${shapeType};labelPosition=${labelPosition};align=center;fontColor=${fontColor}`;
    const vertex = new mxCell(label, new mxGeometry(0, 0, width, height), style);
    vertex.setVertex(true);
    vertex.library = library;
    vertex.shapeType = shapeType;

    insertModeRef.current = true;
    insertCellRef.current = vertex;

    const ghost = document.createElement("img");
    ghost.src = iconPath;
    ghost.style.position = "absolute";
    ghost.style.width = `${width}px`;
    ghost.style.height = "30px";
    ghost.style.pointerEvents = "none";
    ghost.style.opacity = "0.6";
    ghost.style.zIndex = "9999";

    const container = containerRefs.current;
    if (container) {
      container.appendChild(ghost);
      previewRef.current = ghost;
    }

    let lastX = 0, lastY = 0;

    const moveGhost = () => {
      if (!container || !previewRef.current) return;

      const { scrollLeft, scrollTop } = container;
      const { left, top } = container.getBoundingClientRect();

      const x = lastX - left + scrollLeft - ghost.offsetWidth * 0.5;
      const y = lastY - top + scrollTop - ghost.offsetHeight * 0.5;

      ghost.style.left = `${x}px`;
      ghost.style.top = `${y}px`;
    };

    // Save handlers to window so they can be removed later
    window.moveGhostHandler = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      moveGhost();
    };

    window.scrollGhostHandler = () => moveGhost();

    // Attach event listeners
    window.addEventListener("mousemove", window.moveGhostHandler);
    container.addEventListener("scroll", window.scrollGhostHandler);
  };

  const handlePinCellSelect = (data) => {
    clearPinPlacement();
    if (!data) {
      // Cleanup if null or undefined is passed
      insertModeRef.current = false;
      insertCellRef.current = null;

      if (previewRef.current) {
        previewRef.current.remove();
        previewRef.current = null;
      }

      return; // Stop further execution
    }

    const { shapeType, imagePath, labels, library } = data;
    handlePinClick(shapeType, imagePath, labels, library);
  };

  const handlePinRename = (newName) => {
    if (!selectedGraphCell || newName === selectedGraphCell.value) return;

    const oldName = selectedGraphCell.value;

    if (pinMapRef.current?.[newName]) {
      console.warn("Pin name already exists.");
      return;
    }

    const graph = graphRefs.current;
    if (!graph) return;

    const model = graph.getModel();
    model.beginUpdate();
    try {
      model.setValue(selectedGraphCell, newName);
      // Cascade rename to all *explicit* net edge labels that used this pin name.
      const trimmedNewName = (newName || '').trim();
      graph.getChildEdges(graph.getDefaultParent()).forEach(edge => {
        if (edge.internal) return;
        const ev = model.getValue(edge);
        if (typeof ev !== 'string') return;
        const trimmed = ev.trim();
        if (!trimmed) return;
        // Only rename user-visible labels; skip hidden auto nets (noLabel=1)
        if ((edge.style || '').includes('noLabel=1')) return;
        if (trimmed !== oldName) return;

        // If pin name is cleared, revert this net back to an auto name (net_#)
        // instead of leaving a blank edge value.
        if (!trimmedNewName) {
          applyNetLabel(graph, edge, '', isDarkMode, netCountersRef, 'stick');
        } else {
          model.setValue(edge, trimmedNewName);
        }
      });

      // Keep *explicit* port net-labels consistent with pin rename.
      // Only update ports whose value is a non-empty string exactly equal to oldName.
      const parent = graph.getDefaultParent();
      const allVertices = graph.getChildVertices(parent) || [];
      allVertices.forEach((v) => {
        const childPorts = graph.getChildVertices(v) || [];
        childPorts.forEach((p) => {
          const pv = model.getValue(p);
          if (typeof pv !== 'string') return;
          const trimmed = pv.trim();
          if (!trimmed) return;
          if (trimmed === oldName) model.setValue(p, newName);
        });
      });

      graph.refresh();
    } finally {
      model.endUpdate();
    }

    if (pinMapRef.current?.[oldName]) {
      pinMapRef.current[newName] = pinMapRef.current[oldName];
      pinMapRef.current[newName].cell = pinMapRef;
      delete pinMapRef.current[oldName];
    }
  };

  //To get all constraints of a cell. (This method is used to get the constraints)
  function getAllConstraints(graph, cell) {
    const state = graph.view.getState(cell);
    if (!state) return [];

    // Uses the built-in constraint handler
    const constraints = graph.getAllConnectionConstraints(state) || [];
    return constraints;
  }

  function isConstraintPointConnected(graph, cell, constraint) {
    let connected = false;
    // Loop through edges of this cell
    const edgeCells = graph.getModel().getEdges(cell);
    
    if (edgeCells) {
      for (let edgeCell of edgeCells) {
        
        const srcCell = graph.getModel().getTerminal(edgeCell, true);
        const trgCell = graph.getModel().getTerminal(edgeCell, false);
        const edgeState = graph.getView().getState(edgeCell);

        let sc, tc;
        if (srcCell === cell) {
          sc = graph.getConnectionConstraint(edgeState, graph.view.getState(cell), true);
          // check if sc matches the constraint
        }

        if (trgCell === cell) {
          tc = graph.getConnectionConstraint(edgeState, graph.view.getState(cell), false);
          // check if tc matches the constraint
        }
        // Compare with this constraint
        if (
          (sc && sc.point && sc.point.equals(constraint.point)) ||
          (tc && tc.point && tc.point.equals(constraint.point))
        ) {
          connected = true;
          if (constraint.name === "G" && connected) {
              console.log("gate Edge", edgeCell);
          }

          break;
        }
      }
    }      
    let shape = cell.style.split(';')[0].trim();
    let cellType = shape.slice(shape.indexOf("=") + 1);

    return {
      celltype: cellType,
      cellname: cell.value,
      constraint: constraint.name,
      point: constraint.point,
      connected,
    };
  }

  const checkAndSave = async () => {
    const graph = graphRefs.current;
    const model = graph.getModel();
    var validatedConstraints = [];

    model.cells &&
      Object.values(model.cells).forEach((cell) => {
        if (cell.id == 0 || cell.id == 1) {
          //skipping root cells
          //console.log("root cells");
        } else if (cell.edge) {
          //edge only cells checking with edge attribute
          //console.log("skipping edge cells");
        } else if (cell.vertex) {
          //vertex only cells. checking vertex attribute
          const constraints = getAllConstraints(graph, cell);
          constraints.forEach((constraint, index) => {
            const constraintConnected = isConstraintPointConnected(graph, cell, constraint);  
            validatedConstraints.push(constraintConnected);           
            
          });
        }
      });
      return validatedConstraints;
  };

  const handleShowLVS = () => {
    setOpenLVSDialog(true);
  };

  return (
    <Box className="stick-diagram-body">
      <Box className="stick-diagram-header-container">
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="#">
            {libName}
          </Link>
          <Link underline="hover" color="inherit" href="#">
            {cellName}
          </Link>
          <Typography color="text.primary">{stickDiagName}</Typography>
        </Breadcrumbs>
        <Box className="stick-diagram-header">
          {/* <BorderVerticalIcon fontSize="small" className="stick-icon" /> */}
          <Typography variant="h2" sx={{ display: { xs: 'none', sm: 'block' }, padding:'3px' }} className="sniglet-font-text">
            PHYtool
          </Typography>
          <Avatar className="stick-avatar" onClick={handleAvatarClick}>
            {getInitials(empName)}
          </Avatar>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleAvatarClose} disableAutoFocusItem>
            <Box sx={{ padding:'5px' }}>
              <Typography className="stick-menu-text">
                Hello <strong>{empName}</strong>
              </Typography>
            </Box>
          </Menu>
        </Box>
      </Box>

      <Box className="stick-diagram-toolbar-container">
        <Box className="instance-pin-buttons">
          <Button variant="contained" color="primary" className='customButtonStyle' onClick={handleOpenAddInstance}>Add Instance</Button>
          <Button variant="contained" color="primary" className='customButtonStyle' onClick={handleOpenAddPin}>Add Pin</Button>
          {isInstanceMinimized && (
            <MinimizedBar title="Add Instance" onRestore={handleRestoreInstance} onClose={handleCloseInstance} />
          )}
        </Box>
        <Box className="toolbar-content">
          {graphReady && commonRefs.current && (
            <GraphToolbar
              graph={graphRefs.current}
              containerRef={containerRefs.current}              
              commonRef={commonRefs.current}
              isDarkMode={isDarkMode}
              toggleMode={toggleMode}
              isSelectionEmpty={isSelectionEmpty}
              hasClipboardContent={hasClipboardContent}
              handleSave={handleSave}
              saveEnabled={saveEnabled}
              handleEdgeWidthChange={handleEdgeWidthChange}
              handleEdgeColorChange={handleEdgeColorChange}                            
              checkAndSave= {checkAndSave}
              handleOutlineVisible={handleOutlineVisible}
              onShowNetlist={handleShowNetlist}
               diagramMode="stickDiagram"
              onValidate={handleShowLVS}
            />
          )}
        </Box>
      </Box>
      <ThemeProvider theme={localTheme}>
        <CssBaseline />
        <Grid
          id="main-graph"
          ref={(el) => (containerRefs.current = el)}
          tabIndex={0}
          onContextMenu={handleContextMenu}
          className="graph-container"
        >
          {/* {containerRefs.current && (
            <ContextMenu
              commonRef={commonRefs.current}
              visible={menuVisible}
              position={menuPosition}
              containerRef={{ current: containerRefs.current }}
              onClose={handleCloseMenu}
              onMenuClick={handleMenuClick1}
              isSelectionEmpty={isSelectionEmpty}
            />
          )} */}
        </Grid>
        <Box
        className="outline-container"
          id="outlineContainer"
          sx={{
            display: isOutlineVisible ? "block" : "none",
            position: "absolute",
            bottom: 10,
            right: 20,
            width: "220px",
            height: '140px',
            backgroundColor: "transparent",
            border: `2px solid ${isDarkMode ? '#ffffff' : '#000000'}`,
            zIndex: 1000,
            pointerEvents: "auto", 
          }}
          ref={(el) => {
            if (el) outlineRef.current = el;
          }}
        />
      </ThemeProvider>
      <AddInstanceDialog open={openAddInstance}
        onClose={handleCloseInstance}
        onHide={handleHideInstance}
        key={dialogKey} 
        onCellViewSelect={handleCellViewSelect}
        instanceName={nextInstanceName}
        onInstanceRename={handleInstanceRename}
        isEdit={isEditInstance}
        editInstanceName={editInstanceName}
        instancesMapRef={instancesMapRef} 
        validateInstanceName={(name) => isInstanceNameAvailable(name, isEditInstance ? selectedGraphCell : null)}
        viewTypeId={VIEW_TYPE_ID} />
      <AddPinDialog open={openAddPin} onClose={() => setOpenAddPin(false)}
        onCellSelect={handlePinCellSelect}
        isEdit={isEditPin}
        editPinName={editPinName}
        onPinRename={handlePinRename}
        editPinShapeType={selectedGraphCell?.shapeType || ''}
      />

      {/* Net label dialog */}
      <Dialog open={openNetLabelDialog} onClose={handleNetLabelClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '14px', pb: 1 }}>Rename Net (Wire)</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Net name"
            value={netLabelValue}
            onChange={(e) => setNetLabelValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNetLabelConfirm(); }}
            placeholder="e.g. VDD, VSS, IN, OUT"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNetLabelClose} size="small">Cancel</Button>
          <Button onClick={handleNetLabelConfirm} variant="contained" size="small">Apply</Button>
        </DialogActions>
      </Dialog>

      <NetlistDialog
        open={openNetlistDialog}
        onClose={() => setOpenNetlistDialog(false)}
        netlistText={netlistData}
        onCopy={handleCopyNetlist}
        isDarkMode={isDarkMode}
      />
      <ValidatorDialog
        open={openLVSDialog}
        onClose={() => setOpenLVSDialog(false)}
        cellId={cell_id}
        stickViewId={viewId}
        isDarkMode={isDarkMode}
      />
    </Box>
  );
}

export default StickDiagram;
